// ============================================================
//  PORTFOLIO CONFIG — edit everything here
//  All personal info, content, links, and data in one place.
// ============================================================

// ---------- Site & SEO ----------
export const site = {
  url: "https://nivyan.dev",
  name: "Nivyan",
  domain: "nivyan.dev",
  title: "Nivyan — AI Developer & AWS Solutions Architect",
  description:
    "AI developer and AWS Certified Solutions Architect with 6+ years shipping production LLM apps and AWS serverless systems. Open for contract and remote roles.",
  // Topical terms — fed into the Person.knowsAbout JSON-LD field.
  // These are what crawlers and LLMs use to understand expertise areas.
  knowsAbout: [
    "TypeScript",
    "JavaScript",
    "React",
    "Next.js",
    "Node.js",
    "Tailwind CSS",
    "GraphQL",
    "WebSockets",
    "Python",
    "FastAPI",
    "Django",
    "PostgreSQL",
    "Redis",
    "Celery",
    "LangChain",
    "Anthropic Claude",
    "Claude API",
    "OpenAI GPT",
    "OpenAI API",
    "Google Gemini",
    "Generative AI",
    "Agentic AI",
    "Natural Language to SQL",
    "Retrieval-Augmented Generation",
    "Vector Databases",
    "Embeddings",
    "AI Agents",
    "Model Context Protocol",
    "Prompt Engineering",
    "LLM Evaluations",
    "Tool Use",
    "Structured Outputs",
    "AWS Lambda",
    "AWS Cloud Architecture",
    "AWS EventBridge",
    "AWS DynamoDB",
    "AWS S3",
    "AWS Textract",
    "AWS CloudFormation",
    "Terraform",
    "Serverless Architecture",
    "Multi-tenant SaaS",
    "Cloud-native Engineering",
    "Distributed Systems",
  ],
  // Minimal meta keywords — Google ignores them, but Yandex/Baidu still parse.
  keywords: [
    "AI developer",
    "AWS Solutions Architect",
    "AWS certified solutions architect",
    "hire AI developer",
    "hire AWS solutions architect",
    "AI engineer",
    "LLM integration developer",
    "RAG pipeline developer",
    "AI agent developer",
    "LangChain developer",
    "Claude API developer",
    "MCP developer",
    "AWS cloud architect",
    "full-stack AI developer",
    "Next.js developer",
    "remote AI developer",
  ],
  ogImage: "/og-image.png",
  // Drives sitemap lastModified and llms.txt freshness signals.
  // Keep in sync with the latest `updatedAt` across `caseStudies` — this
  // should always equal (or postdate) the newest case-study edit.
  updatedAt: "2026-07-17",
};

// ---------- Personal ----------
export const personal = {
  name: "Nivyan",
  tagline: "AI Developer & AWS Solutions Architect",
  // Rendered inside the hero <h1> (with a decorative "// " prefix) so the
  // primary keyword phrase lives in the page's H1, not just a styled div.
  heroComment: "AI Developer & AWS Solutions Architect",
  heroHeadline: "Nivyan",
  heroPitch:
    "I design AWS architecture and build production AI systems, LLM integrations, and full-stack apps that don't page anyone at 3am.",
  // Short supporting line rendered directly under the hero comment, above heroPitch.
  heroDetail:
    "SAA-C03 certified · 6+ years shipping production AI systems and AWS architecture.",
  // Longer-form bio used in JSON-LD Person.description and as the heavy-facts
  // paragraph LLMs extract for "tell me about Nivyan" queries.
  bio:
    "AI developer and AWS Certified Solutions Architect with 6+ years across production AI/LLM systems and cloud-native architecture. Recent work: Promptly (Socratic AI product architect, 16 metered agents behind a four-gate cost guard, FastAPI + Anthropic Claude + Next.js + 4 real-time WebSocket rooms), SBA Loans HQ (four-portal SBA lending SaaS, Next.js + Django REST + AWS Textract + Gemini, $33M+ in loans processed), and Quanta (self-hosted AI BI platform, a multi-stage natural-language-to-SQL pipeline across Gemini, OpenAI, Claude, and Groq with SELECT-only enforcement and SSH bastion tunneling). Stack: TypeScript, React, Next.js, Python, FastAPI, LangChain, AWS Lambda, EventBridge, DynamoDB, PostgreSQL. Available for contract, consulting, and full-time remote roles across EMEA and US-East timezones.",
  portfolioVersion: "Portfolio v3.0 — 2026",

  // Location & availability
  location: "Lahore, PK",
  timezone: "UTC +5",
  timezoneIANA: "Asia/Karachi",
  timezoneLabel: "PKT",
  yearsOfExperience: "6+ years",
  status: "Available" as "Available" | "Busy" | "Not Available",

  // Contact
  email: "hello@nivyan.dev",
  cvPath: "/cv.pdf",
};

// ---------- Navigation ----------
export const navLinks = [
  { href: "/", label: "Index" },
  { href: "/work", label: "Work" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
  // { href: "/writing", label: "Writing" }, // hidden for now
];

// ---------- Social links ----------
export const socials = [
  { label: "GitHub", href: "https://github.com/nivyanb" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/nivyanb/" },
  { label: "Discord", href: "https://discord.com/users/532573516294520844" },
];

// ---------- Footer ----------
export const footerBio =
  "AI developer and AWS Solutions Architect building production LLM integrations, AWS serverless systems, and Next.js applications that ship.";

// ---------- Hero marquee ----------
export const marqueeWords = [
  "React",
  "JavaScript",
  "TypeScript",
  "Python",
  "AWS Lambda",
  "CloudFormation",
  "OpenAI",
  "S3",
  "EventBridge",
  "Next.js",
  "FastAPI",
  "Terraform",
  "Node.js",
  "Vector DBs",
  "Tailwind",
  "GraphQL",
  "LangChain",
  "Embeddings",
  "EC2",
  "DynamoDB",
];

// ---------- Terminal (hero) ----------
export const terminal = {
  user: "Nivyan · AI Developer · AWS Solutions Architect",
  focus: ["web", "AI/ML", "cloud"],
  cloud: "AWS Serverless",
  statusValue: true,
};

// ---------- AWS Certification ----------
// Verified via Credly: https://www.credly.com/badges/9e8e9a75-f5a7-4509-bf89-f11f36244650/public_url
export const awsCert = {
  credentialId: "9e8e9a75-f5a7-4509-bf89-f11f36244650",
  displayId: "9E8E9A75",
  issued: "July 2026",
  expires: "July 2029",
  verifyUrl: "https://www.credly.com/badges/9e8e9a75-f5a7-4509-bf89-f11f36244650/public_url",
  verifyLabel: "credly.com/verify",
  certName: "AWS Certified Solutions Architect",
  certLevel: "Certified 2026",
  certDescription:
    "Foundational certification covering architecture best practices, cost optimization, high availability, and service selection across AWS compute, storage, networking, and security domains.",
};

// ---------- About section ----------
export const about = {
  eyebrow: "01 / About",
  heading: ["I build things", "at the edge of", "what's possible."],
  // dropcap = first letter of quote; before/emphasis/after = the three parts of the sentence
  quoteDrop: "I",
  quoteBefore:
    "'ve been engineering software for six years, from solo freelance builds to leading delivery on multi-tenant SaaS. The throughline: ",
  quoteEmphasis:
    "AI systems and AWS architecture that hold up in production, not just in the demo",
  quoteAfter: ".",
  bodyParagraph:
    "I work across the full stack: LLM integrations, Retrieval-Augmented Generation pipelines, and distributed AWS serverless backends, with production experience in React, TypeScript, Next.js, Python, FastAPI, and LangChain.",
  bodyParagraphTwo:
    "Recent work includes Promptly (a Socratic AI product architect with 16 metered agents behind a four-gate cost guard), SBA Loans HQ (a four-portal SBA lending platform with $33M+ in loans processed), and Quanta (a natural-language-to-SQL BI tool spanning four LLM providers).",
};

// ---------- Skills / Stack ----------
export type SkillNode = {
  label: string;
  x: number;
  y: number;
  primary?: boolean;
  size?: number;
};

export const skillCategories: {
  title: string;
  nodes: SkillNode[];
  color: string;
  aws: boolean;
}[] = [
  {
    title: "Web & Frontend",
    nodes: [
      { label: "React", x: 200, y: 130, primary: true, size: 9 },
      { label: "Next.js", x: 120, y: 70 },
      { label: "TypeScript", x: 280, y: 80 },
      { label: "Tailwind", x: 110, y: 180 },
      { label: "Node.js", x: 290, y: 180 },
      { label: "GraphQL", x: 200, y: 220 },
    ],
    color: "#c9c4ba",
    aws: false,
  },
  {
    title: "AI & Backend",
    nodes: [
      { label: "Python", x: 200, y: 130, primary: true, size: 9 },
      { label: "LangChain", x: 110, y: 70 },
      { label: "OpenAI", x: 290, y: 80 },
      { label: "FastAPI", x: 100, y: 180 },
      { label: "Embeddings", x: 300, y: 180 },
      { label: "HuggingFace", x: 200, y: 220 },
    ],
    color: "#c9c4ba",
    aws: false,
  },
  {
    title: "Cloud & AWS",
    nodes: [
      { label: "Lambda", x: 200, y: 130, primary: true, size: 10 },
      { label: "EC2", x: 110, y: 60 },
      { label: "S3", x: 290, y: 70 },
      { label: "DynamoDB", x: 100, y: 190 },
      { label: "CloudFormation", x: 300, y: 200 },
      { label: "EventBridge", x: 200, y: 230 },
    ],
    color: "#FF6B1A",
    aws: true,
  },
];

// ---------- Projects ----------
export type Project = {
  num: string;
  tag: string;
  title: string;
  desc: string;
  stack: string[];
  accent?: boolean;
  href?: string;
};

export const projects: Project[] = [
  {
    num: "01",
    tag: "AI / Product",
    title: "Promptly",
    desc:
      "Socratic AI product architect: 16 agents propose, deterministic code decides — a four-gate cost guard, an acyclic dependency graph, and a living PRD from one spec store.",
    stack: ["FastAPI", "Next.js", "PostgreSQL", "Anthropic Claude"],
    accent: true,
    href: "/work/promptly",
  },
  {
    num: "02",
    tag: "FinTech / SBA Lending",
    title: "SBA Loans HQ",
    desc:
      "Four-portal SBA lending SaaS: blind deal marketplace, 10-stage enforced pipeline, AI document intake — $33M+ in loans processed.",
    stack: ["Next.js", "Django REST", "PostgreSQL", "Gemini"],
    href: "/work/sba-loans-hq",
  },
  {
    num: "03",
    tag: "AI / Data",
    title: "Quanta",
    accent: true,
    desc:
      "Self-hosted AI BI: ask in plain English, a six-stage pipeline writes provably read-only SQL — even against databases behind SSH bastions.",
    stack: ["React", "FastAPI", "4× LLM", "PostgreSQL"],
    href: "/work/quanta",
  },
  // {
  //   num: "04",
  //   tag: "Cloud / AWS",
  //   title: "Lambda Observability Platform",
  //   desc: "Real-time distributed telemetry for serverless workloads. Ingests 14M events/day with sub-100ms overhead.",
  //   stack: ["AWS Lambda", "CloudWatch", "TypeScript", "DynamoDB"],
  //   href: "/work/lambda-observability-platform",
  // },
  // {
  //   num: "05",
  //   tag: "AI / Backend",
  //   title: "LLM Code Review Bot",
  //   desc: "GitHub-integrated PR reviewer powered by GPT-4. Reduces review cycles by 40% while catching edge-case bugs.",
  //   stack: ["OpenAI", "LangChain", "Python", "GitHub API"],
  //   href: "/work/llm-code-review-bot",
  // },
  // {
  //   num: "06",
  //   tag: "Cloud / IaC",
  //   title: "Natural Language IaC Generator",
  //   desc: "Converts plain-English specs to production-ready Terraform and CloudFormation. Supports 60+ AWS resource types.",
  //   stack: ["AWS", "Terraform", "GPT-4", "FastAPI"],
  // },
];

// ---------- Experience ----------
export type Job = {
  years: string;
  role: string;
  org: string;
  notes: string;
};

export const experience: Job[] = [
  {
    years: "2025 — Present",
    role: "Full-Stack AI Engineer & Product Manager",
    org: "SBA Loans HQ · Arithmiks",
    notes:
      "Took dual ownership of engineering and product direction on a multi-tenant SBA lending platform. Shipped AI-powered document processing, financial audit automation, loan lead spam filtering, and approval prediction tools across the full loan lifecycle. Over $33 million in loans approved through the platform, with a substantial pipeline of active deals in progress.",
  },
  {
    years: "2024 — 2025",
    role: "Full-Stack Developer",
    org: "Swerv Automotive · Arithmiks",
    notes:
      "Built a predictive lead intelligence platform for automotive dealerships. Analysed vehicle lifecycle and purchase history data to surface high-intent buyers before they enter the market, delivering ranked prospect lists with likelihood scores so dealerships can act ahead of demand.",
  },
  {
    years: "2023 — 2024",
    role: "Front-End Developer",
    org: "Easybar · Arithmiks",
    notes:
      "Delivered a full-stack rebar procurement platform that replaced fragmented manual workflows with a unified digital system. Built a canvas-based interactive design tool for rebars, wire mesh, and pile cages with real-time visual feedback; automated quotation generation and print-ready fabrication ticket delivery; branded PDF and Excel exports with RTL/LTR language support; centralised order tracking with automatic weight and timeline calculations; and a side-by-side bid comparison tool for procurement decisions.",
  },
  {
    years: "2020 — 2025",
    role: "Independent Software Consultant",
    org: "Contract · Remote",
    notes:
      "Ongoing freelance practice alongside the full-time roles above: 20+ end-to-end products delivered for global clients across healthcare, e-commerce, CAD tooling, and 3D visualization, on time and within scope.",
  },
];

// ---------- Case Studies ----------
export type ProcessPhase = {
  phase: string;
  description: string;
};

export type ResultStat = {
  stat: string;
  label: string;
};

export type NodeType = 'client' | 'server' | 'database' | 'api' | 'service' | 'external';

export type ArchNode = {
  id: string;
  label: string;
  type: NodeType;
  sublabel?: string;
  x: number;  // 0–100 percentage across the canvas
  y: number;  // 0–100 percentage down the canvas
};

export type ArchEdge = {
  from: string;
  to: string;
  label?: string;
  direction?: 'unidirectional' | 'bidirectional';
  style?: 'solid' | 'dashed';
  offset?: number;  // perpendicular offset (SVG units) — useful for parallel edges
};

export type ArchGroup = {
  label: string;
  nodeIds: string[];
};

export type CaseStudy = {
  slug: string;
  tag: string;
  title: string[];
  accentLine: string;
  description: string;
  role: string;
  timeline: string;
  // ISO date used in JSON-LD CreativeWork.dateModified and the sitemap.
  updatedAt: string;
  // ISO date used in JSON-LD CreativeWork.dateCreated.
  createdAt: string;
  stack: string[];
  hidden?: boolean;
  coverImage?: string;
  liveUrl?: string;
  architecture?: {
    nodes: ArchNode[];
    edges: ArchEdge[];
    groups?: ArchGroup[];
  };
  sections: {
    overview: { quote: string; body: string };
    myRole: string;
    // Optional feature grid rendered between Architecture and Process.
    // `icon` keys into the FEATURE_ICONS map in the case study page.
    features?: { name: string; description: string; icon?: string }[];
    // Optional product-tour gallery of annotated screenshots, rendered as its
    // own "Screens" section. `image` is a /public path keyed into the
    // screenImageMap in the case study page (static import → blur + dimensions).
    gallery?: { name: string; caption: string; image: string }[];
    process: ProcessPhase[];
    results: ResultStat[];
  };
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "promptly",
    tag: "AI / Product",
    coverImage: "/promptly-case.png",
    title: ["PROMPTLY", "SOCRATIC", "SPECS"],
    accentLine: "SOCRATIC",
    description:
      "An AI product architect that Socratically interviews founders and turns the conversation into an engineering-grade spec — modules, an acyclic dependency graph, a living PRD, a consistency score. 16 AI agents propose; deterministic code decides, behind a four-gate cost guard that makes overspend structurally impossible.",
    role: "Architect & Full-Stack Engineer",
    timeline: "2024 — Present",
    createdAt: "2024-09-01",
    updatedAt: "2026-07-17",
    stack: [
      "FastAPI",
      "Next.js",
      "PostgreSQL",
      "React 19",
      "SQLAlchemy 2.0",
      "Redis",
      "Anthropic Claude",
      "Tailwind CSS 4",
      "TypeScript",
      "WebSockets",
    ],
    architecture: {
      nodes: [
        { id: 'browser',         label: 'Browser',         type: 'client',   sublabel: 'httpOnly · silent refresh',    x: 3,  y: 50 },
        { id: 'nextjs',          label: 'Next.js 16',      type: 'client',   sublabel: 'React 19 · 3 deps',            x: 15, y: 50 },
        { id: 'fastapi',         label: 'FastAPI',         type: 'server',   sublabel: 'api/ routers · N instances',   x: 33, y: 50 },
        { id: 'redis',           label: 'Redis 7',         type: 'service',  sublabel: 'pub/sub · Lua locks',          x: 12, y: 85 },
        { id: 'postgres',        label: 'PostgreSQL 17',   type: 'database', sublabel: 'only durable store',           x: 42, y: 85 },
        { id: 'chat_agents',     label: 'Chat Agents',     type: 'service',  sublabel: 'Ember · retry · summary',      x: 47, y: 18 },
        { id: 'detail_agents',   label: 'Detail Agents',   type: 'service',  sublabel: 'Bank Planner · Validator',     x: 61, y: 18 },
        { id: 'spec_agents',     label: 'Spec Agents',     type: 'service',  sublabel: 'Extractor · Dep. Analyzer',    x: 75, y: 18 },
        { id: 'review_agents',   label: 'Review Agents',   type: 'service',  sublabel: 'Cross-Module Reviewer',       x: 89, y: 18 },
        { id: 'build_agents',    label: 'Build Agents',    type: 'service',  sublabel: 'Stack · Wireframe Gen.',       x: 64, y: 84 },
        { id: 'anthropic',       label: 'Anthropic API',   type: 'external', sublabel: 'gated + metered',              x: 94, y: 50 },
      ],
      edges: [
        { from: 'browser',        to: 'nextjs',        label: 'HTTPS' },
        { from: 'nextjs',         to: 'fastapi',       label: 'REST + WS · 4 rooms', offset: -14 },
        { from: 'nextjs',         to: 'fastapi',       label: '',                          direction: 'bidirectional', style: 'dashed', offset: 14 },
        { from: 'fastapi',        to: 'redis',         label: 'pub/sub · locks · buckets', style: 'dashed' },
        { from: 'fastapi',        to: 'postgres',      label: 'SQLAlchemy 2.0 async' },
        { from: 'fastapi',        to: 'anthropic',     label: 'core/claude.py · cost_guard', style: 'dashed' },
        { from: 'fastapi',        to: 'chat_agents',   label: 'overview → modules' },
        { from: 'chat_agents',    to: 'detail_agents', label: '→ detailing' },
        { from: 'detail_agents',  to: 'spec_agents',   label: '→ finalizing' },
        { from: 'spec_agents',    to: 'review_agents', label: '→ review' },
        { from: 'fastapi',        to: 'build_agents',  label: 'on-demand · after specs exist', style: 'dashed' },
      ],
      groups: [
        { label: 'Frontend',        nodeIds: ['browser', 'nextjs'] },
        { label: 'API Server',      nodeIds: ['fastapi'] },
        { label: 'Coordination',    nodeIds: ['redis'] },
        { label: 'Data',            nodeIds: ['postgres'] },
        { label: 'Agent Pipeline · 16', nodeIds: ['chat_agents', 'detail_agents', 'spec_agents', 'review_agents'] },
        { label: 'On-Demand',       nodeIds: ['build_agents'] },
        { label: 'External',        nodeIds: ['anthropic'] },
      ],
    },
    sections: {
      overview: {
        quote:
          '"The AI interviews you; the server owns every decision it proposes."',
        body: "Founders show up with a vibe, not a spec: \"an app like X but for Y,\" and discovery burns weeks of expensive back-and-forth that produces prose, not artifacts. Promptly runs a guided six-phase workflow instead: Ember, the Socratic architect agent, interviews the founder while 16 AI agents propose modules, questions, dependency edges, and specs through typed tool calls that deterministic code validates, applies, or silently drops. The model never assigns an ID, never moves a phase, and can never overspend past a four-gate cost guard sitting in front of every call. The output stays synchronized: a dependency graph and a living PRD are two projections of one spec store, a consistency score recomputes on every mutation, and snapshots make every risky operation reversible.",
      },
      myRole:
        "I architected and built Promptly end to end: the FastAPI/SQLAlchemy backend, all 16 agent pipelines, and the Next.js/React frontend hand-rolled on three runtime dependencies. The hardest problems were structural, not visual — drawing the \"agents propose, code decides\" boundary so a hallucinating model can never corrupt state (server-assigned IDs, a legal-transition graph, cycle detection on every edge); building the four-gate cost guard that makes overspend structurally impossible before a call ever fires; and making the whole system horizontally scalable from day one, with every coordination primitive — locks, buckets, streaks, pub/sub fan-out — living in Redis so N backend instances behave as one.",
      features: [
        {
          name: "Socratic Chat — Ember",
          icon: "chat",
          description:
            "Every message runs one guarded, metered, streaming turn against a 7-tool contract; when a turn produces no real side-effect, a forced-tool retry and then a deterministic fallback question mean the conversation can never dead-end.",
        },
        {
          name: "Session Security Beyond Tutorial Tier",
          icon: "lock",
          description:
            "httpOnly cookies with refresh-token family rotation — a replayed token is treated as theft and revokes the entire family, committed before the 401 so a rollback can't undo it — plus enumeration-safe login with a dummy bcrypt verify.",
        },
        {
          name: "16 Agents, Zero State Ownership",
          icon: "layers",
          description:
            "The Architect, Bank Planner, Extractor, Dependency Analyzer, Conflict Detector, and 11 more only propose via typed tool calls; the model never assigns an ID or moves a phase, and illegal requests are silently dropped against a legal transition graph.",
        },
        {
          name: "Six-Phase State Machine",
          icon: "grid",
          description:
            "overview → modules → detailing → finalizing → review → complete moves through one guarded function with a full audit trail — from, to, trigger, rationale — on every transition.",
        },
        {
          name: "One-Click AI Suggestions",
          icon: "sparkle",
          description:
            "Flagged vague terms and conflicts get one AI-generated fix — one measurable definition, one reconciliation sentence — alongside a Tech-Stack Matchmaker and a Wireframer that generate build-support artifacts grounded in the real spec.",
        },
        {
          name: "Four-Gate Cost Guard",
          icon: "shield",
          description:
            "Account standing, per-user budget, per-project $5 cap, and a hard call ceiling all clear before a single model call fires; a per-call ledger prices every token, and an unpriced model refuses to boot rather than silently mis-bill.",
        },
        {
          name: "Real-Time Everywhere",
          icon: "pulse",
          description:
            "Four WebSocket rooms per project — chat, graph, document, score — fan out through Redis pub/sub; a per-session outbox flushes only on commit, so a rolled-back transaction never broadcasts a lie.",
        },
        {
          name: "One Spec Store, Two Projections",
          icon: "doc",
          description:
            "The dependency graph and the living PRD can't drift — editing a document section is editing the graph node, and cycle detection on every edge insert keeps the graph provably acyclic.",
        },
        {
          name: "Restorable by Design",
          icon: "table",
          description:
            "Module-slice and whole-project snapshots auto-taken before every risky operation; every restore takes its own safety snapshot first, so restores are themselves reversible.",
        },
      ],
      process: [
        {
          phase: "The trust boundary — agents propose, code decides",
          description:
            "Designed the core guarantee before writing a single agent prompt: every one of 16 pipelines speaks only through typed tool calls, and deterministic code validates, applies, or drops them against a legal transition graph, server-assigned FEAT-NNN codes, and cycle detection on every edge. A model that hallucinates an ID or requests an illegal phase jump is simply ignored, not caught after the fact.",
        },
        {
          phase: "The cost and failure envelope",
          description:
            "Built the four-gate cost guard — account standing, per-user budget, per-project cap, hard call ceiling — in front of every model call, with a per-call ledger pricing fresh, cached, and output tokens against a price table that refuses to boot on an unpriced model. Paired it with a designed fallback for every one of the 16 pipelines — retry, draft recovery, manual finalize, spec-less finalize — so a failed call degrades gracefully instead of wedging the state machine.",
        },
        {
          phase: "Real-time at scale, then hardening",
          description:
            "Moved all coordination into Redis — Lua-scripted token buckets, compare-and-delete lock release, TTL'd streak counters — so N backend instances share zero in-process state while fanning four WebSocket rooms per project through pub/sub. Closed with a 30-flaw adversarial review of the chat pipeline, 60+ end-to-end flow tests including live reconnects across backend restarts, and a 63-entry decision log tying every non-obvious choice back to code.",
        },
      ],
      results: [
        { stat: "16", label: "AI agents, one spend gate" },
        { stat: "~60", label: "live events, 4 WS rooms" },
        { stat: "$5", label: "hard project cap, per-call ledger" },
        { stat: "60+", label: "flows tested, 30-flaw self-audit" },
      ],
    },
  },
  {
    slug: "sba-loans-hq",
    tag: "FinTech / SBA Lending",
    coverImage: "/sba-loans-hq-case.png",
    liveUrl: "https://www.sbaloanshq.com/",
    title: ["SBA", "LOANS", "HQ"],
    accentLine: "HQ",
    description:
      "Multi-tenant SaaS running the full SBA acquisition-financing lifecycle: four portals from one codebase, a blind-listing deal marketplace with e-sign-gated PII release, a 10-stage enforced pipeline, and AI document intake — $33M+ in loans processed.",
    role: "Full-Stack Developer & Team Lead",
    timeline: "2024 – Present",
    createdAt: "2024-09-01",
    updatedAt: "2026-07-16",
    stack: [
      "Next.js (Pages)",
      "Django REST",
      "PostgreSQL / RDS",
      "React 19",
      "Redux Toolkit",
      "Tailwind CSS",
      "Celery + Redis",
      "AWS Textract",
      "Google Gemini",
      "AWS S3 · EC2 · Lambda",
      "DocuSeal",
      "@dnd-kit",
      "TipTap",
      "ApexCharts",
      "Postmark",
      "Mixpanel",
      "GA4 / GTM",
    ],
    architecture: {
      nodes: [
        { id: 'browser',  label: 'Browser',       type: 'client',   sublabel: 'Redux · JWT',          x: 3,  y: 50 },
        { id: 'nextjs',   label: 'Next.js 16',    type: 'client',   sublabel: 'Pages Router · SSR',   x: 18, y: 50 },
        { id: 'nginx',    label: 'Nginx',         type: 'server',   sublabel: 'TLS · Certbot',        x: 35, y: 30 },
        { id: 'django',   label: 'Django + DRF',  type: 'server',   sublabel: 'JWT · RBAC · v0/',     x: 35, y: 70 },
        { id: 'redis',    label: 'Redis',         type: 'service',  sublabel: 'Broker + Results',    x: 55, y: 25 },
        { id: 'celery',   label: 'Celery',        type: 'service',  sublabel: 'Workers · Beat',      x: 55, y: 80 },
        { id: 'postgres', label: 'PostgreSQL',    type: 'database', sublabel: 'psycopg2 · MatViews', x: 73, y: 20 },
        { id: 's3',       label: 'AWS S3',        type: 'service',  sublabel: 'boto3 · 3 Buckets',   x: 73, y: 62 },
        { id: 'textract', label: 'AWS Textract',  type: 'api',      sublabel: 'OCR Engine',          x: 90, y: 14 },
        { id: 'gemini',   label: 'Gemini',        type: 'external', sublabel: 'google-genai SDK',    x: 90, y: 38 },
        { id: 'docuseal', label: 'DocuSeal',      type: 'external', sublabel: 'JWT builder tokens',  x: 90, y: 63 },
        { id: 'postmark', label: 'Postmark',      type: 'external', sublabel: 'stage-driven email',  x: 90, y: 86 },
      ],
      edges: [
        { from: 'browser',  to: 'nextjs',   label: 'HTTPS' },
        { from: 'nextjs',   to: 'nginx',    label: 'HTTP/2',         direction: 'bidirectional' },
        { from: 'nginx',    to: 'django',   label: 'proxy_pass :8000' },
        { from: 'django',   to: 'redis',    label: 'Task.delay()',   style: 'dashed' },
        { from: 'django',   to: 'postgres', label: 'psycopg2 ORM' },
        { from: 'django',   to: 's3',       label: 'pre-signed URL', style: 'dashed' },
        { from: 'django',   to: 'docuseal', label: 'per-request JWT', style: 'dashed' },
        { from: 'redis',    to: 'celery',   label: 'BRPOP' },
        { from: 'celery',   to: 'postgres', label: 'save results' },
        { from: 'celery',   to: 's3',       label: 'multipart PUT' },
        { from: 'celery',   to: 'textract', label: 'boto3 client',   style: 'dashed' },
        { from: 'celery',   to: 'postmark', label: 'outbound email', style: 'dashed' },
        { from: 'textract', to: 'gemini',   label: 'OCR chunks',     style: 'dashed' },
      ],
      groups: [
        { label: 'Frontend',       nodeIds: ['browser', 'nextjs'] },
        { label: 'API Layer',      nodeIds: ['nginx', 'django'] },
        { label: 'Async Layer',    nodeIds: ['redis', 'celery'] },
        { label: 'Data Store',     nodeIds: ['postgres', 's3'] },
        { label: 'AI Pipeline',    nodeIds: ['textract', 'gemini'] },
        { label: 'E-sign & Email', nodeIds: ['docuseal', 'postmark'] },
      ],
    },
    sections: {
      overview: {
        quote:
          '"Borrowers, brokers, lenders, and outside requestors — one pipeline instead of four disconnected inboxes."',
        body: "SBA acquisition-financing consultancies run deals through email threads, spreadsheets, and CRMs that don't understand underwriting — no shared pipeline truth, manual document chasing, and no way to shop a deal to lenders without leaking the borrower's identity. SBA Loans HQ replaces all of it: four portal experiences from one codebase, a 10-stage pipeline whose rules are enforced in the UI itself, stage-gated document bundles, and a deal marketplace where lenders evaluate anonymized deals and borrower PII is released only after a referral agreement clears e-signature. It's a live production platform — ~12 months of weekly shipping and $33M+ in loans processed.",
      },
      myRole:
        "I led full-stack delivery as team lead across ~12 months of continuous production shipping — 3,849 commits and 798 merged PRs. Owned the Next.js frontend (426 components, 75 routes across four portal experiences) against a Django REST + Celery backend on AWS, including the marketplace's agreement-gated access model, the qualification rule engine, and the embedded DocuSeal integration. Delivered with Arithmiks as the consulting partner to SBA Loans HQ.",
      features: [
        {
          name: "Four Portals, One Codebase",
          icon: "layers",
          description:
            "Staff, borrower, lender, and external-requestor experiences ship from a single Next.js app — a useBrandTerms hook swaps 'Lender' for 'Approver' across the entire UI for white-labeled partners, so nobody maintains a fork.",
        },
        {
          name: "Blind-Listing Deal Marketplace",
          icon: "lock",
          description:
            "Lenders evaluate deals as anonymized metrics — loan size, credit profile, terms — and borrower identity is released only after the referral agreement clears e-signature, enforced by an AwaitingAgreement state machine.",
        },
        {
          name: "Lead Qualification Rule Engine",
          icon: "filter",
          description:
            "Auto-qualify and disqualify rules over 15 underwriting fields, AND within a rule and OR across rules — with server-side rejection of unsatisfiable or mutually conflicting rule sets, not just form validation.",
        },
        {
          name: "Stage-Enforced Kanban",
          icon: "grid",
          description:
            "Drag-and-drop with the business rules resolved before the API call fires: forward-only moves through 10 stages, automatic hidden-stage skipping, and optimistic updates that roll back if the move is rejected.",
        },
        {
          name: "Embedded E-Signature",
          icon: "pen",
          description:
            "DocuSeal's builder embedded on short-lived, per-request JWT tokens — the client never holds a standing API key — with 30-second status polling that self-cancels the moment a document hits a terminal state.",
        },
        {
          name: "Stage-Gated Document Bundles",
          icon: "doc",
          description:
            "Each deal auto-requests the right financial documents for its stage and loan purpose; outside accountants and attorneys get a scoped, searchable dashboard for their single ask.",
        },
        {
          name: "Dynamic Financial Memorandum",
          icon: "table",
          description:
            "A year-column spreadsheet view that reshapes whatever periods the backend returns into business, debt, and personal blocks with an auto-computed SDE row — and re-lays out for lender-facing PDF export.",
        },
        {
          name: "Per-Deal Chat",
          icon: "chat",
          description:
            "A threaded drawer scoped to each deal — participants, history, composer — replacing the email back-and-forth brokers were drowning in.",
        },
        {
          name: "Six-Domain Analytics",
          icon: "pulse",
          description:
            "Deal, lead, lender, partner, staff, and requestee metrics on ApexCharts, plus an origination funnel deliberately built Leads → Deals → Closed so it narrows monotonically even when deals skip the lead stage.",
        },
        {
          name: "Defense-in-Depth RBAC",
          icon: "shield",
          description:
            "Staff, lender, and partner roles enforced server-side and again in the client — brokers are hard-limited to Leads and Deals, with off-limits nav, bundle actions, and tours hidden outright rather than disabled.",
        },
      ],
      process: [
        {
          phase: "Domain Modeling & Multi-Tenancy",
          description:
            "Mapped how SBA consultancies actually close deals — DSCR, SDE, stage-gated documents, referral agreements — into a product model no generic CRM covers. Structured the frontend as four independently-routed portals over one design system, with RBAC enforced in the API and enforced again in the client's own navigation.",
        },
        {
          phase: "Workflow Enforcement & AI Intake",
          description:
            "Encoded the 10-stage lifecycle as enforced behavior, not convention: forward-only Kanban, stage-gated document auto-requests, and the marketplace's e-sign-gated PII release. Layered AWS Textract OCR and Gemini over document intake so financial PDFs become structured data, intelligent file names, and lead scores instead of attachments.",
        },
        {
          phase: "Production Hardening at Velocity",
          description:
            "Kept a live platform shippable for ~12 months: a 268-code centralized error taxonomy behind a single useErrorHandler pattern, a database-backed error log with full user context, and self-terminating status polling everywhere. Marketplace phase 2 — agreement-gated access — shipped within a month of phase 1.",
        },
      ],
      results: [
        { stat: "$33M+", label: "Loans processed" },
        { stat: "4 portals", label: "One codebase" },
        { stat: "Zero PII", label: "Shared before e-sign" },
        { stat: "10-stage", label: "Enforced deal pipeline" },
      ],
    },
  },
  {
    slug: "quanta",
    tag: "AI / Data",
    coverImage: "/quanta-case.png",
    liveUrl: "https://quanta.arithmiks.com/",
    title: ["QUANTA", "AI", "DASHBOARD"],
    accentLine: "AI",
    description:
      "Self-hosted AI business intelligence in one command. A six-stage natural-language-to-SQL pipeline across four LLM providers — with SELECT-only safety gating, SSH bastion tunneling, AES-256-GCM credential vaulting, and end-to-end AI trace observability.",
    role: "Architect & Full-Stack Engineer",
    timeline: "3 months (2026) — solo, end-to-end",
    createdAt: "2026-04-16",
    updatedAt: "2026-07-16",
    stack: [
      "React",
      "TypeScript",
      "Vite",
      "Tailwind CSS",
      "FastAPI",
      "SQLAlchemy 2",
      "PostgreSQL",
      "MySQL",
      "Recharts",
      "Zustand",
      "TanStack Query",
      "react-grid-layout",
      "Google Gemini",
      "OpenAI",
      "Anthropic Claude",
      "Groq",
      "Paramiko (SSH)",
      "Docker",
      "Nginx",
    ],
    architecture: {
      nodes: [
        { id: 'browser',  label: 'Browser',       type: 'client',   sublabel: 'httpOnly · CSRF pair',  x: 3,  y: 50 },
        { id: 'spa',      label: 'React SPA',     type: 'client',   sublabel: 'TanStack · Recharts',   x: 14, y: 50 },
        { id: 'nginx',    label: 'Nginx',         type: 'server',   sublabel: 'serves /dist · proxy',  x: 30, y: 35 },
        { id: 'traces',   label: 'AI Traces',     type: 'service',  sublabel: 'trace-id · ai.log',     x: 30, y: 75 },
        { id: 'fastapi',  label: 'FastAPI',       type: 'server',   sublabel: 'JWT · slowapi · RBAC',  x: 45, y: 35 },
        { id: 'registry', label: 'Provider Reg.', type: 'service',  sublabel: 'runtime toggles · caps',x: 45, y: 72 },
        { id: 'vault',    label: 'AES Vault',     type: 'service',  sublabel: 'AES-256-GCM creds',     x: 61, y: 22 },
        { id: 'tunnel',   label: 'SSH Tunnel',    type: 'service',  sublabel: 'paramiko · TOFU pin',   x: 61, y: 60 },
        { id: 'appdb',    label: 'App PostgreSQL',type: 'database', sublabel: 'SQLAlchemy 2 · Alembic',x: 79, y: 14 },
        { id: 'userdbs',  label: 'User DBs',      type: 'database', sublabel: 'Postgres · MySQL · pvt',x: 79, y: 40 },
        { id: 'gemini',   label: 'Gemini',        type: 'external', sublabel: 'google-genai SDK',      x: 74, y: 82 },
        { id: 'openai',   label: 'OpenAI',        type: 'external', sublabel: 'GPT models',            x: 90, y: 82 },
        { id: 'claude',   label: 'Claude',        type: 'external', sublabel: 'anthropic SDK',         x: 74, y: 98 },
        { id: 'groq',     label: 'Groq',          type: 'external', sublabel: 'fast inference',        x: 90, y: 98 },
      ],
      edges: [
        { from: 'browser',  to: 'spa',      label: 'HTTPS · SPA bundle' },
        { from: 'spa',      to: 'nginx',    label: 'REST · Axios + RQ' },
        { from: 'nginx',    to: 'fastapi',  label: 'proxy_pass /api' },
        { from: 'fastapi',  to: 'appdb',    label: 'SQLAlchemy 2' },
        { from: 'fastapi',  to: 'vault',    label: 'decrypt creds' },
        { from: 'fastapi',  to: 'registry', label: 'NL → SQL pipeline' },
        { from: 'fastapi',  to: 'traces',   label: 'per-stage trace-id',   style: 'dashed' },
        { from: 'vault',    to: 'tunnel',   label: 'SSH keys · secrets' },
        { from: 'tunnel',   to: 'userdbs',  label: 'bastion forward',      style: 'dashed' },
        { from: 'registry', to: 'gemini',   label: 'per-query pick',       style: 'dashed' },
        { from: 'registry', to: 'openai',   label: '',                     style: 'dashed' },
        { from: 'registry', to: 'claude',   label: '',                     style: 'dashed' },
        { from: 'registry', to: 'groq',     label: '',                     style: 'dashed' },
      ],
      groups: [
        { label: 'Frontend',             nodeIds: ['browser', 'spa'] },
        { label: 'Edge & Observability', nodeIds: ['nginx', 'traces'] },
        { label: 'API Server',           nodeIds: ['fastapi', 'registry'] },
        { label: 'Security Layer',       nodeIds: ['vault', 'tunnel'] },
        { label: 'Data',                 nodeIds: ['appdb', 'userdbs'] },
        { label: 'LLM Providers',        nodeIds: ['gemini', 'openai', 'claude', 'groq'] },
      ],
    },
    sections: {
      overview: {
        quote:
          '"Ask your database a question in plain English — Quanta plans, writes, safety-checks, runs, and explains the SQL."',
        body: "Teams are stuck between raw SQL tools that exclude most of the company and enterprise BI suites that route data through someone else's cloud. Quanta closes the gap: a self-hosted analytics platform where natural language is a first-class query interface — not a wrapper, but a six-stage pipeline (plan → clarify → generate → safety-gate → self-heal → summarize) that asks instead of guessing when a name is ambiguous. Four LLM providers sit behind one interface, every AI call is traced and cost-capped, generated SQL is provably read-only, and private databases behind SSH bastions are the normal case, not a deal-breaker.",
      },
      myRole:
        "Sole architect and engineer, end to end: FastAPI + SQLAlchemy 2 backend, React 19 + TypeScript SPA, and one-command Docker Compose deployment behind Nginx. Designed the staged NL→SQL pipeline across Gemini, OpenAI, Claude, and Groq; the security model (AES-256-GCM vaulting, SSH tunneling with TOFU host-key pinning, SELECT-only enforcement); and the trace-ID observability that follows every AI request from the browser into an admin trace explorer.",
      features: [
        {
          name: "Natural-Language Queries",
          icon: "chat",
          description:
            "Ask in plain English; a six-stage pipeline plans, asks clarifying questions when names are ambiguous, generates dialect-correct SQL, self-heals failures, and explains the answer.",
        },
        {
          name: "SSH Bastion Tunneling",
          icon: "lock",
          description:
            "Reaches private-subnet databases most lightweight tools can't touch — encrypted key material, TOFU host-key pinning, and hard-fail on a swapped bastion key.",
        },
        {
          name: "Four AI Providers",
          icon: "layers",
          description:
            "Gemini, OpenAI, Claude, and Groq behind one interface: users switch per query, admins toggle at runtime, every call metered against hard budget caps.",
        },
        {
          name: "Dashboard Builder",
          icon: "grid",
          description:
            "12-column drag-and-drop grid, 11 chart types, autosaving layouts — and any AI answer becomes a live widget in one click.",
        },
        {
          name: "AI Dashboard Recommendations",
          icon: "sparkle",
          description:
            "Point it at a schema and get a configured dashboard. Every suggestion is vetted against the cached schema, so a hallucinated table can never render.",
        },
        {
          name: "Workspaces & Roles",
          icon: "users",
          description:
            "Multi-tenant organizations with owner/member roles, one-click invite codes, and per-workspace monthly AI spend caps enforced server-side.",
        },
        {
          name: "Explorer & Query Studio",
          icon: "terminal",
          description:
            "Parameterized, injection-proof table browsing for non-SQL users; a raw SQL editor for the fluent — both behind the same read-only safety gate.",
        },
        {
          name: "Public Dashboard Sharing",
          icon: "share",
          description:
            "One toggle publishes a dashboard to an unguessable URL — strictly read-only, fresh data per view, zero credential surface.",
        },
        {
          name: "AI Trace Observability",
          icon: "pulse",
          description:
            "Trace IDs minted in the browser follow every request through each pipeline stage; support debugs any AI failure from one pasted ID.",
        },
        {
          name: "Hardened Auth",
          icon: "shield",
          description:
            "HttpOnly JWT cookies, CSRF double-submit, hashed revocable refresh tokens, timing-safe login, and spoof-resistant rate limiting.",
        },
      ],
      process: [
        {
          phase: "Security & Private-Network Layer",
          description:
            "Built the layer most BI side projects never touch: paramiko SSH tunnels with trust-on-first-use host-key pinning, an SSRF split policy (backend dials only public bastions; private databases resolve bastion-side), and AES-256-GCM encryption on every stored credential.",
        },
        {
          phase: "The AI Pipeline & Cost Engineering",
          description:
            "Designed the six-stage NL→SQL pipeline — plan, clarify, generate, safety-gate, self-heal, summarize — with static SELECT-only verification before anything executes. A two-tier schema serializer cut input-token cost 40–50%, measured and applied uniformly across all four providers.",
        },
        {
          phase: "Dashboards, Teams & Observability",
          description:
            "Shipped the product around the pipeline: workspaces with roles and AI budgets, persistent chat sessions docked beside dashboards, public read-only sharing, and trace-ID observability running from the browser console to an admin trace explorer.",
        },
      ],
      results: [
        { stat: "40–50%", label: "AI token cost reduction" },
        { stat: "4 LLMs", label: "One provider interface" },
        { stat: "6-stage", label: "Read-only AI SQL pipeline" },
        { stat: "TOFU-pinned", label: "SSH bastion tunnels" },
      ],
    },
  },
  {
    slug: "lambda-observability-platform",
    tag: "Cloud / AWS",
    hidden: true,
    title: ["LAMBDA", "OBSERVABILITY", "PLATFORM"],
    accentLine: "PLATFORM",
    description:
      "Real-time distributed telemetry for AWS Lambda serverless workloads. Ingests 14M events/day with sub-100ms overhead, EventBridge fan-out, and DynamoDB Streams aggregation.",
    role: "Lead Engineer",
    timeline: "Q1 2024",
    createdAt: "2024-01-15",
    updatedAt: "2024-04-01",
    stack: [
      "AWS Lambda",
      "CloudWatch",
      "DynamoDB",
      "TypeScript",
      "React",
      "Terraform",
      "SNS",
      "EventBridge",
    ],
    architecture: {
      nodes: [
        { id: 'fns',  label: 'Lambda Fns',   type: 'service',  sublabel: '200+ functions', x: 10, y: 50 },
        { id: 'eb',   label: 'EventBridge',  type: 'api',      sublabel: 'Fan-out',         x: 38, y: 50 },
        { id: 'ddb',  label: 'DynamoDB',     type: 'database', sublabel: 'Streams',         x: 65, y: 25 },
        { id: 'cw',   label: 'CloudWatch',   type: 'service',  sublabel: 'Metrics',         x: 65, y: 75 },
        { id: 'dash', label: 'React Dash',   type: 'client',   sublabel: 'Dashboard',       x: 90, y: 50 },
      ],
      edges: [
        { from: 'fns', to: 'eb',   label: 'Async',   style: 'dashed' },
        { from: 'eb',  to: 'ddb',  label: 'Events' },
        { from: 'eb',  to: 'cw',   label: 'Metrics' },
        { from: 'ddb', to: 'dash', label: 'Streams' },
        { from: 'cw',  to: 'dash', label: 'Alerts' },
      ],
    },
    sections: {
      overview: {
        quote:
          '"Real-time Lambda telemetry at scale, without waking anyone up."',
        body: "Our team was operating a fleet of 200+ Lambda functions with zero unified observability. CloudWatch logs were scattered, alert fatigue was high, and debugging a production incident meant manually correlating logs across a dozen functions. I designed and built a centralised telemetry platform that aggregates, correlates, and surfaces Lambda execution data in real time — ingesting 14M events per day with sub-100ms overhead.",
      },
      myRole:
        "Led end-to-end architecture, implementation, and delivery as the sole platform engineer. Defined the data model, designed the ingestion pipeline, built the React dashboard, and owned the Terraform infrastructure. Worked closely with the on-call team to shape alerting logic that actually reduced pages.",
      process: [
        {
          phase: "Discovery & Constraints",
          description:
            "Interviewed on-call engineers to map current pain points. Established hard constraints: sub-100ms per-invocation overhead, zero cold-start impact, ops-free maintenance. Evaluated three architectures before landing on an EventBridge fan-out pattern.",
        },
        {
          phase: "Design & Architecture",
          description:
            "Designed a layered system: a Lambda layer for zero-config telemetry emission, EventBridge for async fan-out, DynamoDB Streams for real-time aggregation, and a React dashboard backed by a serverless API. Full diagram reviewed by three senior engineers before cutting a line of code.",
        },
        {
          phase: "Build & Iterate",
          description:
            "Shipped the ingestion layer first, validated volume assumptions with a load test, then built the dashboard. Ran two internal beta cycles before enabling for all Lambda functions. Iterated on alert thresholds with the ops team over 3 weeks until fatigue dropped measurably.",
        },
      ],
      results: [
        { stat: "98%", label: "Uptime SLA" },
        { stat: "↓ 40%", label: "Alert Fatigue" },
        { stat: "3×", label: "Faster Debug" },
      ],
    },
  },
  {
    slug: "llm-code-review-bot",
    tag: "AI / Backend",
    hidden: true,
    title: ["LLM CODE", "REVIEW", "BOT"],
    accentLine: "BOT",
    description:
      "GitHub-integrated LLM pull request reviewer built with LangChain and OpenAI GPT-4. Cuts review cycles by 40% while catching edge-case bugs human reviewers miss.",
    role: "Lead Engineer",
    timeline: "Q3 2024",
    createdAt: "2024-07-01",
    updatedAt: "2024-10-01",
    stack: ["OpenAI", "LangChain", "Python", "GitHub API", "FastAPI", "Redis"],
    sections: {
      overview: {
        quote: '"The first reviewer that never sleeps and never skips the diff."',
        body: "Our engineering team was bottlenecked on code reviews — PRs sat for hours, senior engineers spent cognitive energy on mechanical checks, and edge-case bugs slipped through. I built a GitHub-integrated LLM review bot that runs on every PR, performs context-aware analysis, and posts structured comments — reducing review cycles by 40%.",
      },
      myRole:
        "Sole engineer from concept to production. Designed the LangChain pipeline, integrated with the GitHub API, implemented Redis caching for diff context, and deployed to AWS Lambda. Defined the evaluation rubric and tuned prompts through 200+ real PR test cases.",
      process: [
        {
          phase: "Evaluation & Prompt Design",
          description:
            "Gathered 50 past PRs with known bugs and manually graded outputs across 6 prompt strategies. Settled on a chain-of-thought rubric covering correctness, security, style, and test coverage — with per-language calibration.",
        },
        {
          phase: "Pipeline Architecture",
          description:
            "Built a LangChain pipeline with diff chunking, cross-file context retrieval via Redis, and structured output parsing. GitHub Webhooks trigger the Lambda; results post as inline review comments within 30s of PR open.",
        },
        {
          phase: "Tuning & Rollout",
          description:
            "Ran a 3-week shadow mode alongside human reviewers to measure agreement rate. Tuned false-positive rate down to under 8% before enabling auto-block on critical findings. Rolled out to all 12 engineers with a kill switch.",
        },
      ],
      results: [
        { stat: "↓ 40%", label: "Review Cycles" },
        { stat: "< 8%", label: "False Positive Rate" },
        { stat: "30s", label: "Time to Review" },
      ],
    },
  },
];

// ---------- Contact section ----------
export const contact = {
  eyebrow: "07 / Contact",
  heading: ["Let's build", "something", "worth shipping."],
  subtext:
    "Available for senior individual contributor roles, AWS Solutions Architecture consulting, AI/LLM integration work, and 0→1 MVP builds. Remote-first across EMEA and US-East timezones. Inbox is fast.",
  ctaLabel: "Schedule a call",
  ctaHref: "https://calendly.com/bnivyan09",
};

// ---------- Services (engagement types) ----------
// Used on /contact page Service schema and as extractable hire-me copy for LLMs.
export const services = [
  {
    name: "AI development & LLM integration",
    description:
      "Adding production-grade LLM features to existing apps: Retrieval-Augmented Generation pipelines, agent workflows, structured outputs, tool use, prompt evals, and provider failover across Anthropic Claude, OpenAI GPT, and Google Gemini.",
  },
  {
    name: "AWS Solutions Architecture & migration",
    description:
      "Designing or migrating workloads to AWS serverless as an AWS Certified Solutions Architect: Lambda, EventBridge, DynamoDB, S3, CloudFormation, Terraform.",
  },
  {
    name: "Full-stack web build (0→1)",
    description:
      "End-to-end product delivery: Next.js or React frontend, FastAPI or Django backend, PostgreSQL, authentication, observability, and CI/CD. Multi-tenant SaaS a specialty.",
  },
  {
    name: "Production hardening & rescue",
    description:
      "Take a flaky prototype and ship it: zero-downtime deploys, observability, cost optimization, security review, and on-call playbooks.",
  },
  {
    name: "Senior IC & full-time remote roles",
    description:
      "Open to senior software engineer, staff engineer, and tech lead positions in AI, cloud, or product engineering teams. Remote, EMEA/US-East friendly.",
  },
];

// ---------- FAQ ----------
// Used in FAQPage JSON-LD and rendered as a semantic <dl> on home/contact.
export const faq = [
  {
    q: "What kind of engineering work do you take on?",
    a: "Production AI/LLM features (RAG, agents, structured outputs across Claude / GPT / Gemini), AWS serverless architecture, and full-stack web builds with Next.js, React, FastAPI, and PostgreSQL. Strongest on multi-tenant SaaS, AI-augmented workflows, and cloud-native systems where reliability matters.",
  },
  {
    q: "Are you available for full-time roles?",
    a: "Yes, open to senior software engineer, staff engineer, or tech lead positions, remote-first, ideally with EMEA or US-East timezone overlap. Also open to fractional and contract engagements.",
  },
  {
    q: "Can I hire you as a freelance AI developer or AWS Solutions Architect?",
    a: "Yes, I take freelance and contract work as an AI developer and AWS Certified Solutions Architect: LLM integration (Claude, GPT, Gemini), RAG pipelines, AI agents, and AWS serverless builds. Engage directly by email, through Contra, or as a fractional senior engineer.",
  },
  {
    q: "What's a typical engagement length?",
    a: "Contract work usually runs 4–16 weeks for a focused build (LLM feature integration, MVP, or AWS migration), or ongoing as a fractional senior engineer. Full-time roles are open-ended.",
  },
  {
    q: "What timezones do you work across?",
    a: "Based in Lahore, Pakistan (UTC+5 / Asia-Karachi). Standard overlap with EMEA all day and US-East mornings. Comfortable shifting hours for sync-heavy clients.",
  },
  {
    q: "Do you have AWS certifications?",
    a: "AWS Certified Solutions Architect (SAA-C03), issued July 2026, valid through July 2029. Production experience across Lambda, EventBridge, DynamoDB, S3, RDS, Textract, CloudFormation, and Terraform.",
  },
  {
    q: "Which LLM and AI tools have you shipped to production?",
    a: "Anthropic Claude (Sonnet and Opus), OpenAI GPT-4, Google Gemini, LangChain, embeddings and vector retrieval, AWS Textract for OCR, and custom evals. Recent work: Promptly (a Claude-powered Socratic product architect with a four-gate AI cost guard and 16 metered agent pipelines) and Quanta (natural-language SQL across Gemini, OpenAI, Claude, and Groq with SELECT-only enforcement).",
  },
  {
    q: "How do I scope a project quickly?",
    a: "Email hello@nivyan.dev with one paragraph on the problem, current stack, and timing — or book a 30-minute call at calendly.com/bnivyan09. I respond within a business day.",
  },
];
